import { Button, Container, Flex, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { IFileForm } from 'api/types';
import { AxiosResponse } from 'axios';
import { useDeleteB2CServiceDocument } from 'hooks/mutations/useDeleteB2CServiceDocumentMutation';
import useUploadB2CFileMutation from 'hooks/mutations/useUploadB2CFileMutation';
import { Dispatch, FC, SetStateAction } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { dropzone } from '../helpers/makeFormFields';

type FileFormProps = {
  uploadFileMutation: UseMutationResult<AxiosResponse<any, any>, any, IFileForm, unknown> | ReturnType<typeof useUploadB2CFileMutation>;
  deleteServiceDocumentMutation: UseMutationResult<AxiosResponse<any, any>, any, void, unknown> | ReturnType<typeof useDeleteB2CServiceDocument>;
  uploadFile?: (data: any, setError: any) => void;
  sendRequest?(x: object): void;
  isPendingParent?: boolean;
  buttonText?: string;
  editCancelButton?: any;
  isDisabled?: boolean;
  setCanEdit?: Dispatch<SetStateAction<boolean>>;
  serviceDocumentExists?: boolean;
};

const schema = yup
  .object({
    file: yup.mixed().required(),
  })
  .required();

const FileForm: FC<FileFormProps> = ({
  sendRequest,
  uploadFileMutation,
  deleteServiceDocumentMutation,
  buttonText,
  editCancelButton,
  isDisabled = false,
  setCanEdit,
  serviceDocumentExists,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    control,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate, isPending } = useMutation<any, Error, IFileForm>({
    mutationFn: async (data) => {
      const formData = new FormData();
      // @ts-ignore
      formData.append('file', data.file.file as File);
      return sendRequest ? sendRequest(formData) : null;
    },
  });

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    if (uploadFileMutation && deleteServiceDocumentMutation && setCanEdit) {
      // @ts-expect-error
      uploadFileMutation.mutate(data, {
        onSuccess: () => {
          if (serviceDocumentExists) {
            deleteServiceDocumentMutation.mutate(undefined, {
              onSuccess: () => setCanEdit(false),
            });
          }
        },
        onError: (error: any) => {
          const { errors } = error.response.data;

          (Object.keys(errors) as Array<keyof any>).forEach((key) => {
            // @ts-ignore
            setError(String(key), {
              type: 'server',
              message: errors[key]!.join('. '),
            });
          });
        },
      });
    } else {
      // @ts-expect-error
      mutate(data, {
        onError: (error: any) => {
          const { errors } = error.response.data;
          (Object.keys(errors) as Array<keyof IFileForm>).forEach((key) => {
            setError(key, {
              type: 'server',
              message: errors[key]!.join('. '),
            });
          });
        },
      });
    }
  };
  return (
    <Container as="form" onSubmit={handleSubmit(onSubmit)} opacity={isPending ? 0.5 : 1} maxW={'unset'}>
      <Text fontSize="xs" color="muted">
        {t('components:confirmation_card.hint')}
      </Text>
      {dropzone({
        name: 'file',
        disabled: isDisabled,
        //@ts-ignore
        control,
        errors,
      })}
      <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
        <Button
          type="submit"
          variant="primary"
          ml={2}
          data-test-id="file-submit-button"
          disabled={(submitCount! > 0 && !isValid) || isPending || isDisabled}
        >
          {buttonText ?? t('common:submit')}
        </Button>
        {editCancelButton}
      </Flex>
    </Container>
  );
};

export default FileForm;
